import { exportData, downloadBlob, type Fill, type MarketType } from './hyperliquid';

export type ExportContext = 'dashboard' | 'perps' | 'spot';

interface ExportOptions {
  address: string;
  context: ExportContext;
  rangeDays?: number;
}

const generateFilename = (context: ExportContext): string => {
  const date = new Date().toISOString().split('T')[0]; // YYYY-MM-DD format
  const contextMap = {
    dashboard: 'combined-fills',
    perps: 'perps-fills',
    spot: 'spot-fills'
  };
  return `sharpe-exchange-${contextMap[context]}-${date}.csv`;
};

const formatFillForCSV = (fill: Fill, marketType: MarketType): string => {
  const time = new Date(fill.fill_time_ms).toLocaleString('en-US', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hour12: false
  });
  
  const symbol = fill.symbol || fill.coin || '';
  const side = fill.side?.toLowerCase() === 'b' || fill.side?.toLowerCase() === 'buy' ? 'BUY' : 'SELL';
  const size = fill.sz || 0;
  const price = fill.px || 0;
  const notional = fill.notional || (price * size);
  const fee = fill.fee || 0;
  
  // Escape commas and quotes in CSV fields
  const escapeField = (field: string | number) => {
    const str = String(field);
    if (str.includes(',') || str.includes('"') || str.includes('\n')) {
      return `"${str.replace(/"/g, '""')}"`;
    }
    return str;
  };
  
  return [
    escapeField(time),
    escapeField(symbol),
    escapeField(side),
    escapeField(size),
    escapeField(price),
    escapeField(notional),
    escapeField(fee),
    escapeField(marketType === 'perps' ? 'Perps' : 'Spot')
  ].join(',');
};

const generateCSV = (fills: Fill[], marketType: MarketType): string => {
  const headers = 'Time,Symbol,Side,Size,Price,Notional,Fee,Market Type';
  const rows = fills.map(fill => formatFillForCSV(fill, marketType));
  return [headers, ...rows].join('\n');
};

const combineFillsData = (perpsFills: Fill[], spotFills: Fill[]): string => {
  const headers = 'Time,Symbol,Side,Size,Price,Notional,Fee,Market Type';
  
  const perpsRows = perpsFills.map(fill => formatFillForCSV(fill, 'perps'));
  const spotRows = spotFills.map(fill => formatFillForCSV(fill, 'spot'));
  
  // Combine all fills and sort by time (most recent first)
  const allRows = [...perpsRows, ...spotRows].sort((a, b) => {
    const timeA = new Date(a.split(',')[0]).getTime();
    const timeB = new Date(b.split(',')[0]).getTime();
    return timeB - timeA;
  });
  
  return [headers, ...allRows].join('\n');
};

export const exportFillsToCSV = async ({ address, context, rangeDays = 30 }: ExportOptions): Promise<void> => {
  try {
    let csvContent: string;
    
    if (context === 'dashboard') {
      // For dashboard, we need to fetch both perps and spot data
      const [perpsBlob, spotBlob] = await Promise.all([
        exportData(address, rangeDays, 'fills', 'perps'),
        exportData(address, rangeDays, 'fills', 'spot')
      ]);
      
      const perpsText = await perpsBlob.text();
      const spotText = await spotBlob.text();
      
      // Parse the JSON responses to get fills data
      const perpsData = JSON.parse(perpsText);
      const spotData = JSON.parse(spotText);
      
      const perpsFills: Fill[] = perpsData.recentFills || [];
      const spotFills: Fill[] = spotData.recentFills || [];
      
      csvContent = combineFillsData(perpsFills, spotFills);
    } else {
      // For perps or spot, fetch single market type
      const marketType: MarketType = context === 'perps' ? 'perps' : 'spot';
      const blob = await exportData(address, rangeDays, 'fills', marketType);
      const text = await blob.text();
      const data = JSON.parse(text);
      const fills: Fill[] = data.recentFills || [];
      
      csvContent = generateCSV(fills, marketType);
    }
    
    // Create and download the CSV file
    const csvBlob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const filename = generateFilename(context);
    downloadBlob(csvBlob, filename);
    
  } catch (error) {
    console.error('Export failed:', error);
    throw new Error(`Failed to export fills: ${error instanceof Error ? error.message : 'Unknown error'}`);
  }
};

// Helper function to check if export is available for a context
export const isExportAvailable = (context: ExportContext): boolean => {
  // Currently, spot data might not be fully implemented
  // We can adjust this based on actual data availability
  return context === 'dashboard' || context === 'perps';
};
